import styled from "styled-components";
import {radii} from "theme/tokens";

/**
 * Styles for the § 312k BGB cancellation page (kitecorp/transferify-ui#2327).
 *
 * <p>Colours come from `theme.tokens` only, like the unsubscribe page's: this page is reached by people who
 * may never have signed in, so it has to look right in both modes with no persisted preference.
 *
 * <p><strong>Nothing here may de-emphasise the submit control.</strong> OLG Koblenz 2 U 437/23 held that
 * another route to cancel may sit beside the statutory one but must not push it into the background, and
 * "gut lesbar" in Abs. 2 S. 2 and S. 3 Nr. 2 is a legibility requirement on the controls themselves. So
 * the submit button uses the app's primary action surface at full contrast, and the print action below it
 * is a plain link.
 *
 * <p>A `@media print` block is part of the feature rather than polish: § 312k Abs. 3 asks the consumer to be
 * able to store the declaration with its date and time, and the print action in the confirmation view is how
 * this page offers that. The chrome has no business on that sheet.
 */

export const Root = styled.main`
	margin: 0 auto;
	max-width: 640px;
	padding: 48px 16px 72px;
`;

export const Heading = styled.h1`
	color: ${({theme}) => theme.tokens.ink};
	font-size: 28px;
	line-height: 1.2;
	margin: 0 0 12px;
`;

/**
 * The `margin-top` replaces a paragraph, and that is the whole reason for it.
 *
 * The gap above the first field used to be the intro paragraph's own 32px bottom margin; ui#2601 removed
 * that paragraph (§ 312k Abs. 2 S. 3 is exhaustive). 20px here plus the heading's 12px reproduces the same
 * 32px, which is why the spacing sits on the form rather than on the heading: the heading is shared with the
 * confirmation view, whose 12px gap to its card this ticket does not touch.
 */
export const Form = styled.form`
	display: flex;
	flex-direction: column;
	gap: 24px;
	margin-top: 20px;
`;

export const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

export const FieldError = styled.p`
	color: ${({theme}) => theme.tokens.errorInk};
	font-size: 13px;
	line-height: 1.5;
	margin: 0;
`;

/** The kind of termination. A real fieldset, so the group has one accessible name for both radios. */
export const KindGroup = styled.fieldset`
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.card};
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin: 0;
	padding: 16px;
`;

export const KindLegend = styled.legend`
	color: ${({theme}) => theme.tokens.ink};
	font-size: 15px;
	font-weight: 600;
	padding: 0 4px;
`;

/**
 * One termination choice: the radio and its label.
 *
 * A DIV, not a LABEL. A `<label>` wrapping the whole row absorbs whatever else the row holds into each
 * radio's accessible name; the explicit `<label>` below names the option and only the option, which is the
 * string a screen-reader user hears and the one § 312k Abs. 2 S. 3 Nr. 1 lit. a is about.
 */
export const KindOption = styled.div`
	align-items: center;
	color: ${({theme}) => theme.tokens.ink};
	display: flex;
	font-size: 15px;
	gap: 10px;
`;

/** The option's name, and the click target a pointer user expects the words to be. */
export const KindOptionLabel = styled.label`
	cursor: pointer;
`;

export const KindRadio = styled.input`
	accent-color: ${({theme}) => theme.tokens.blue};
	margin: 0;
`;

export const SubmitRow = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

export const FormError = styled.p`
	color: ${({theme}) => theme.tokens.errorInk};
	font-size: 14px;
	line-height: 1.5;
	margin: 0;
`;

export const NextNote = styled.p`
	color: ${({theme}) => theme.tokens.ink2};
	font-size: 13px;
	line-height: 1.6;
	margin: 0;
`;

export const DoneCard = styled.section`
	background: ${({theme}) => theme.tokens.card};
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.card};
	padding: 28px;
`;

export const Received = styled.p`
	color: ${({theme}) => theme.tokens.ink};
	font-size: 15px;
	line-height: 1.6;
	margin: 0 0 24px;
`;

export const SummaryTitle = styled.h2`
	color: ${({theme}) => theme.tokens.ink};
	font-size: 15px;
	font-weight: 600;
	margin: 0 0 8px;
`;

export const Summary = styled.dl`
	display: grid;
	gap: 8px 16px;
	grid-template-columns: max-content 1fr;
	margin: 0 0 24px;

	dt {
		color: ${({theme}) => theme.tokens.ink2};
		font-size: 13px;
	}

	dd {
		color: ${({theme}) => theme.tokens.ink};
		font-size: 14px;
		margin: 0;
		/* A consumer's own words, quoted back: keep the line breaks they typed. */
		white-space: pre-wrap;
		word-break: break-word;
	}
`;

export const PrintAction = styled.button`
	background: none;
	border: 0;
	color: ${({theme}) => theme.tokens.blue};
	cursor: pointer;
	font: inherit;
	padding: 0;
	text-align: left;
	text-decoration: underline;

	/* The print sheet IS the proof; an action the reader cannot act on there is noise. */
	@media print {
		display: none;
	}
`;

/**
 * One row of the declaration summary.
 *
 * `display: contents` so the `dt`/`dd` pair joins the `Summary` grid directly: a wrapper that participated
 * in the grid itself would put both cells in one column. A keyed element is still needed around them,
 * because a fragment cannot carry the key React wants for a mapped pair.
 */
export const SummaryRow = styled.div`
	display: contents;
`;

/**
 * The one field that is NOT a `BaseInput`, and why.
 *
 * `BaseInput` renders MUI's floating `InputLabel`, which shrinks out of the way only once the control reports
 * a value. A native `type="date"` control shows its own `dd.mm.yyyy` placeholder from the first paint, so the
 * label sits on top of that placeholder until the reader focuses the field. "gut lesbar" in § 312k Abs. 2
 * S. 3 Nr. 2 binds the confirmation control itself, and a page whose fields are less legible than its
 * button is not one a consumer can use — so the same expectation is held here. The alternative is teaching
 * `BaseInput` to forward a `shrink` flag, which is a change to every field in the app for the sake of one.
 */
export const NativeLabel = styled.label`
	color: ${({theme}) => theme.tokens.ink};
	font-size: 15px;
`;

export const DateInput = styled.input`
	background: ${({theme}) => theme.tokens.field};
	border: 0;
	border-bottom: 1px solid ${({theme}) => theme.tokens.fieldLine};
	border-radius: ${radii.control} ${radii.control} 0 0;
	color: ${({theme}) => theme.tokens.ink};
	font: inherit;
	padding: 12px;

	&:focus-visible {
		border-bottom-color: ${({theme}) => theme.tokens.focusOutline};
		outline: 2px solid ${({theme}) => theme.tokens.ring};
		outline-offset: 2px;
	}
`;
